import React, { useState } from 'react';
import axios, { API } from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';

const CreateGoalDialog = ({ onGoalCreated }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', target_date: '' });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/goals`, {
        ...formData,
        target_date: new Date(formData.target_date).toISOString(),
      });
      toast.success('Goal created successfully with AI-generated tasks!');
      onGoalCreated(response.data);
      setIsOpen(false);
      setFormData({ title: '', description: '', target_date: '' });
    } catch {
      toast.error('Failed to create goal');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="bg-justb-primary hover:bg-justb-primary/90 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300">
          <Plus className="mr-2 h-4 w-4" />
          New Goal
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]" aria-describedby="goal-form-description">
        <DialogHeader>
          <DialogTitle className="text-justb-primary font-semibold">Create New Life Goal</DialogTitle>
        </DialogHeader>
        <div id="goal-form-description" className="sr-only">
          Form to create a new life goal with AI-generated daily tasks
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="title" className="text-gray-900 font-medium">Goal Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Learn Spanish fluently"
              required
              className="text-gray-900"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description" className="text-gray-900 font-medium">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what you want to achieve and why it's important to you in detail to get the best AI task list."
              rows={3}
              required
              className="text-gray-900"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="target_date" className="text-gray-900 font-medium">Target Date</Label>
            <Input
              id="target_date"
              type="date"
              value={formData.target_date}
              onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
              min={new Date().toISOString().split('T')[0]}
              required
              className="text-gray-900"
            />
          </div>
          <div className="flex justify-end space-x-3">
            <Button type="button" variant="outline" onClick={() => setIsOpen(false)} className="text-gray-900 border-gray-300">
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading} className="bg-justb-primary hover:bg-justb-primary/90">
              {isLoading ? 'Creating...' : 'Create Goal'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateGoalDialog;
